import { useEffect, useState } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { confirmDialog } from '../../lib/confirmDialog';

const parseVideoSource = (url) => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace('www.', '');
    if (host.includes('youtube.com') || host.includes('youtu.be')) {
      let videoId = parsed.searchParams.get('v');
      if (!videoId && parsed.pathname.startsWith('/embed/')) {
        videoId = parsed.pathname.split('/')[2];
      }
      if (!videoId && host === 'youtu.be') {
        videoId = parsed.pathname.substring(1);
      }
      if (!videoId) return null;
      return {
        provider: 'youtube',
        embedUrl: `https://www.youtube.com/embed/${videoId}`
      };
    }
    if (host.includes('vimeo.com')) {
      const segments = parsed.pathname.split('/').filter(Boolean);
      const videoId = segments[segments.length - 1];
      if (!videoId) return null;
      return {
        provider: 'vimeo',
        embedUrl: `https://player.vimeo.com/video/${videoId}`
      };
    }
    return {
      provider: 'external',
      embedUrl: url
    };
  } catch (_) {
    return null;
  }
};

export default function VideoSectionBuilder({ teamId, sectionId, items = [], onItemsUpdate }) {
  const [localItems, setLocalItems] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    videoUrl: ''
  });

  useEffect(() => {
    const filtered = items.filter((item) => item.section_id === sectionId).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    setLocalItems(filtered);
  }, [items, sectionId]);

  const canMutate = Boolean(teamId && sectionId);

  const handleAddItem = async () => {
    if (!canMutate) {
      toast.error('Please save the section before adding videos.');
      return;
    }

    if (!newItem.videoUrl.trim()) {
      toast.error('Video URL is required.');
      return;
    }

    const parsed = parseVideoSource(newItem.videoUrl.trim());
    if (!parsed) {
      toast.error('Unsupported video URL. Provide a valid YouTube or Vimeo link.');
      return;
    }

    setSaving(true);

    try {
      const insertPayload = {
        team_id: teamId,
        section_id: sectionId,
        title: newItem.title.trim() || null,
        description: newItem.description.trim() || null,
        provider: parsed.provider,
        video_url: newItem.videoUrl.trim(),
        embed_url: parsed.embedUrl,
        sort_order: localItems.length,
        visible: true
      };

      const { data, error } = await supabase
        .from('portal_video_items')
        .insert(insertPayload)
        .select()
        .single();

      if (error) {
        throw error;
      }

      const updated = [...localItems, data];
      setLocalItems(updated);
      onItemsUpdate?.(sectionId, updated);
      setNewItem({ title: '', description: '', videoUrl: '' });
      setIsAdding(false);
      toast.success('Video added');
    } catch (error) {
      console.error('Error adding video:', error);
      toast.error('Failed to add video: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateItem = async (itemId, updates) => {
    const payload = { ...updates };
    if (payload.video_url) {
      const parsed = parseVideoSource(payload.video_url);
      if (!parsed) {
        toast.error('Unsupported video URL.');
        return;
      }
      payload.provider = parsed.provider;
      payload.embed_url = parsed.embedUrl;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('portal_video_items')
        .update(payload)
        .eq('id', itemId);

      if (error) throw error;

      const updated = localItems.map((item) => (item.id === itemId ? { ...item, ...payload } : item));
      setLocalItems(updated);
      onItemsUpdate?.(sectionId, updated);
      setEditingId(null);
      toast.success('Video updated');
    } catch (error) {
      console.error('Error updating video:', error);
      toast.error('Failed to update video: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    const confirmed = await confirmDialog('Remove this video?', {
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });

    if (!confirmed) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('portal_video_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      const updated = localItems.filter((item) => item.id !== itemId);
      setLocalItems(updated);
      onItemsUpdate?.(sectionId, updated);
      toast.success('Video removed');
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Failed to delete video: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async (item) => {
    setSaving(true);
    try {
      const nextVisible = !item.visible;
      const { error } = await supabase
        .from('portal_video_items')
        .update({ visible: nextVisible })
        .eq('id', item.id);

      if (error) throw error;

      const updated = localItems.map((video) =>
        video.id === item.id ? { ...video, visible: nextVisible } : video
      );
      setLocalItems(updated);
      onItemsUpdate?.(sectionId, updated);
      toast.success(`Video ${nextVisible ? 'shown' : 'hidden'} on portal`);
    } catch (error) {
      console.error('Error toggling video visibility:', error);
      toast.error('Failed to update visibility: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = async (activeId, overId) => {
    if (activeId === overId) return;

    const fromIndex = localItems.findIndex((item) => item.id === activeId);
    const toIndex = localItems.findIndex((item) => item.id === overId);
    if (fromIndex === -1 || toIndex === -1) return;

    const reordered = [...localItems];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    const updates = reordered.map((item, idx) => ({ ...item, sort_order: idx }));
    setLocalItems(updates);

    setSaving(true);
    try {
      for (const item of updates) {
        await supabase
          .from('portal_video_items')
          .update({ sort_order: item.sort_order })
          .eq('id', item.id);
      }
      onItemsUpdate?.(sectionId, updates);
    } catch (error) {
      console.error('Error reordering videos:', error);
      toast.error('Failed to reorder videos');
      setLocalItems(localItems);
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!active || !over) return;
    handleReorder(active.id, over.id);
  };

  return (
    <div className="space-y-3" aria-busy={saving}>
      <div className="flex items-center justify-between mb-3">
        <label className="block text-sm font-medium text-gray-700">Video Embeds</label>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            + Add Video
          </button>
        )}
      </div>

      {isAdding && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
          <input
            type="text"
            placeholder="Title (optional)"
            value={newItem.title}
            onChange={(e) => setNewItem((prev) => ({ ...prev, title: e.target.value }))}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <textarea
            placeholder="Description (optional)"
            value={newItem.description}
            onChange={(e) => setNewItem((prev) => ({ ...prev, description: e.target.value }))}
            rows={2}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            placeholder="Video URL (YouTube or Vimeo) *"
            value={newItem.videoUrl}
            onChange={(e) => setNewItem((prev) => ({ ...prev, videoUrl: e.target.value }))}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAddItem}
              disabled={saving || !newItem.videoUrl}
              className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : 'Add Video'}
            </button>
            <button
              onClick={() => {
                setIsAdding(false);
                setNewItem({ title: '', description: '', videoUrl: '' });
              }}
              disabled={saving}
              className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {localItems.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">
          No videos yet. Add highlights, recaps, or motivational clips to bring your portal to life.
        </p>
      ) : (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={localItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {localItems.map((item) => (
                <SortableVideoCard key={item.id} item={item}>
                  {({ attributes, listeners }) => (
                    editingId === item.id ? (
                      <VideoEditForm
                        item={item}
                        saving={saving}
                        onCancel={() => setEditingId(null)}
                        onSave={(updates) => handleUpdateItem(item.id, updates)}
                      />
                    ) : (
                      <div>
                        <div className="flex gap-3">
                          <button
                            {...attributes}
                            {...listeners}
                            className="px-2 py-2 text-xs text-gray-500 hover:bg-gray-100 rounded self-start"
                            title="Drag to reorder"
                            aria-label="Drag video to reorder"
                            type="button"
                          >
                            ☰
                          </button>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="inline-flex items-center px-2 py-0.5 text-[11px] rounded bg-blue-100 text-blue-700 capitalize">
                                {item.provider || 'external'}
                              </span>
                              {item.visible === false && (
                                <span className="inline-flex items-center px-2 py-0.5 text-[11px] rounded bg-gray-200 text-gray-600">
                                  Hidden
                                </span>
                              )}
                            </div>
                            <h4 className="font-medium text-gray-900 text-sm">
                              {item.title || 'Untitled video'}
                            </h4>
                            {item.description && (
                              <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                            )}
                            <p className="text-[11px] text-gray-400 mt-2 break-all">{item.video_url}</p>
                          </div>
                        </div>
                        <div className="flex gap-1 mt-3">
                          <button
                            onClick={() => setEditingId(item.id)}
                            disabled={saving}
                            className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleToggleVisibility(item)}
                            disabled={saving}
                            className={`text-xs px-2 py-1 rounded ${
                              item.visible
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            } disabled:opacity-50`}
                          >
                            {item.visible ? '✓ Visible' : '✕ Hidden'}
                          </button>
                          <button
                            onClick={() => handleDeleteItem(item.id)}
                            disabled={saving}
                            className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </SortableVideoCard>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <p className="text-xs text-gray-500">
        Paste links from YouTube or Vimeo to create a sleek video gallery. Videos play directly within your portal.
      </p>
    </div>
  );
}

function SortableVideoCard({ item, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    cursor: isDragging ? 'grabbing' : 'grab'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg p-3 ${item.visible ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-300'} ${
        isDragging ? 'shadow-lg z-10' : ''
      }`}
    >
      {children({ attributes, listeners })}
    </div>
  );
}

function VideoEditForm({ item, onSave, onCancel, saving }) {
  const [title, setTitle] = useState(item.title || '');
  const [description, setDescription] = useState(item.description || '');
  const [videoUrl, setVideoUrl] = useState(item.video_url || '');

  const handleSubmit = () => {
    if (!videoUrl.trim()) {
      toast.error('Video URL is required');
      return;
    }
    onSave({
      title: title.trim() || null,
      description: description.trim() || null,
      video_url: videoUrl.trim()
    });
  };

  return (
    <div className="space-y-2">
      <input
        type="text"
        placeholder="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <textarea
        placeholder="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <input
        type="text"
        placeholder="Video URL"
        value={videoUrl}
        onChange={(e) => setVideoUrl(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <div className="flex gap-2">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

